import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import PrintIcon from '@mui/icons-material/Print';
import axios from 'axios';
import { format } from 'date-fns'; // Added date-fns import for formatting
import { API_BASE_URL } from '../../constants/constants';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

const AdProviderAdList = () => {
  const { can } = usePermissions();
  const { providerId } = useParams();
  const navigate = useNavigate();

  // State variables
  const [ads, setAds] = useState([]);
  const [totalAds, setTotalAds] = useState(0);
  const [page, setPage] = useState(0); // DataGrid pages are zero-based
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [noAdsFound, setNoAdsFound] = useState(false);

  // Filter states
  const [status, setStatus] = useState('All');

  // Snackbar states
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  // Date range for print
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  // Transactions for print (mock data, replace with actual data fetching)
  const [transactions, setTransactions] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);

  useEffect(() => {
    // Validate providerId
    if (!providerId) {
      showSnackbar('Invalid provider ID.', 'error');
      navigate('/ad-providers'); // Redirect to providers list
    } else {
      fetchAds(); // Fetch all ads by default on page load
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, page, pageSize, status]);

  // Function to fetch ads
  const fetchAds = async () => {
    setLoading(true);
    setNoAdsFound(false);

    try {
      // Build query parameters
      const params = {
        page: page + 1, // Backend pages are 1-based
        limit: pageSize,
      };

      // Apply status filter if not "All"
      if (status !== 'All') {
        // Modify status to match backend expectation
        params.status = status === 'Inactive' ? 'inActive' : status.toLowerCase();
      }

      const response = await axios.get(`${API_BASE_URL}/ads/provider/${providerId}`, { params });

      if (response.status === 200) {
        const { ads, total } = response.data;

        if (ads && ads.length > 0) {
          // Map ads to rows with formatted dates
          const mappedAds = ads.map((ad, index) => ({
            id: ad._id,
            slNo: index + 1 + page * pageSize,
            header: ad.header || 'N/A',
            details: ad.details || 'N/A',
            startDate: ad.startDate ? new Date(ad.startDate).toLocaleDateString('en-GB') : 'N/A',
            endDate: ad.endDate ? new Date(ad.endDate).toLocaleDateString('en-GB') : 'N/A',
            status: ad.status === 'inActive' ? 'Inactive' : capitalizeFirstLetter(ad.status),
            adType: ad.adType ? capitalizeFirstLetter(ad.adType) : 'N/A',
          }));

          setAds(mappedAds);
          setTotalAds(total);
          setNoAdsFound(false);
        } else {
          setAds([]);
          setTotalAds(0);
          setNoAdsFound(true);
        }
      } else {
        setAds([]);
        setTotalAds(0);
        setNoAdsFound(true);
        showSnackbar('Failed to fetch ads.', 'error');
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
      setAds([]);
      setTotalAds(0);
      setNoAdsFound(true);
      showSnackbar('Error fetching ads.', 'error');
    }

    setLoading(false);
  };

  // Helper function to capitalize the first letter
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Snackbar handlers
  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setSnackbarMessage('');
  };

  // Handle print functionality
  const handlePrint = () => {
    if (!can('ads.providers.print')) return;
    if (!ads || ads.length === 0) {
      setDialogMessage('No data to print.');
      setOpenDialog(true);
      return;
    }
  
    const doc = new jsPDF('l', 'pt', 'a4'); // Landscape orientation
  
    // Table columns
    const tableColumn = [
      'Sl. No',
      'Header',
      'Details',
      'Start Date',
      'End Date',
      'Status',
      'Ad Type',
    ];
  
    // Table rows
    const tableRows = ads.map((ad) => [
      ad.slNo,
      ad.header,
      ad.details,
      ad.startDate,
      ad.endDate,
      ad.status,
      ad.adType,
    ]);
  
    // Add title
    doc.setFontSize(16);
    doc.text('Ad Report for Provider', doc.internal.pageSize.getWidth() / 2, 30, {
      align: 'center',
    });
  
    // Add subtitle with provider details
    doc.setFontSize(12);
   
   
  
    // Add the table
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 70,
      theme: 'grid', // Simple grid theme
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] }, // White header with black text
      styles: { fontSize: 12 }, // Set font size to 12
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 150 },
        2: { cellWidth: 200 },
        3: { cellWidth: 100 },
        4: { cellWidth: 100 },
        5: { cellWidth: 80 },
        6: { cellWidth: 80 },
      },
    });
  
    // Add a footer with the date and page number
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()}`,
        30,
        doc.internal.pageSize.getHeight() - 10
      );
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - 70,
        doc.internal.pageSize.getHeight() - 10
      );
    }
  
    // Save the PDF
    doc.save('Ad_Report.pdf');
  };
  

  // Define columns for DataGrid
  const columns = [
    { field: 'slNo', headerName: 'Sl No', width: 80 },
    { field: 'header', headerName: 'Header', width: 200 },
    { field: 'details', headerName: 'Details', width: 300 },
    {
      field: 'startDate',
      headerName: 'Start Date',
      width: 150,
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      width: 150,
    },
    { field: 'status', headerName: 'Status', width: 120 },
    {
      field: 'adType',
      headerName: 'Ad Type',
      width: 120,
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
       <Box sx={{ position: 'relative', padding: 2 }}>
                    {/* Place BackButton outside the Grid */}
                    <BackButton />
                    <br/>  <br/>
      {/* Header */}
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4" gutterBottom>
            Ads for Provider
          </Typography>
        </Grid>
        {can('ads.providers.print') && (
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              Print
            </Button>
          </Grid>
        )}
      </Grid>
      <br/>

      {/* Filters */}
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: '20px' }}>
        {/* Status Filter */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Data Grid */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress />
        </Box>
      ) : noAdsFound ? (
        <Typography variant="h6" align="center" color="textSecondary">
          No ads found for the selected filters.
        </Typography>
      ) : (
        <div style={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={ads}
            columns={columns}
            page={page}
            pageSize={pageSize}
            rowsPerPageOptions={[5, 10, 25, 50]}
            rowCount={totalAds}
            pagination
            paginationMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0); // Reset to first page
            }}
            disableSelectionOnClick
          />
        </div>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Dialog for print errors */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Print Error</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary" autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </div>
  );
};

export default AdProviderAdList;